/**
 * ECONOMÍA de Bellotas vía REST protegido (`/api/economy/*`). Toda mutación de
 * saldo es SERVER-AUTHORITATIVE: el cliente sólo dice QUÉ (product_id) o pide
 * "+vídeo" / "recompensa diaria"; el servidor calcula el delta y devuelve el
 * saldo autoritativo resultante. El cliente NUNCA fija su saldo (anti-fraude).
 *
 * Las lecturas reactivas (saldo, ledger, tienda) van por PowerSync (streams
 * `my_wallet` / `shop_catalog`, offline). Estos endpoints son SOLO para MUTAR.
 * Respuestas camelCase (serde `rename_all`).
 */

import { authedRawFetch, SERVER_BASE_URL } from "@/lib/api";

interface AcornTransactionDTO {
  id: string;
  userId: string;
  delta: number;
  reason: string;
  balanceAfter: number;
  refId?: string | null;
  createdAt: string;
}

interface WalletMutationResultDTO {
  balanceAcorns: number;
  transaction: AcornTransactionDTO;
}

interface RedemptionDTO {
  id: string;
  userId: string;
  shopProductId: string;
  costAcorns: number;
  createdAt: string;
}

interface RedeemResultDTO {
  balanceAcorns: number;
  redemption: RedemptionDTO;
  transaction: AcornTransactionDTO;
}

interface DailyRewardClaimDTO {
  id: string;
  userId: string;
  claimedOn: string;
  amount: number;
  streakDay: number;
  createdAt: string;
}

interface DailyRewardResultDTO {
  balanceAcorns: number;
  transaction: AcornTransactionDTO;
  claim: DailyRewardClaimDTO;
}

function economyUrl(path: string): string {
  return `${SERVER_BASE_URL.replace(/\/$/, "")}/api/economy${path}`;
}

/** Error tipado de una petición de economía (lleva el status HTTP). */
class EconomyRequestError extends Error {
  readonly status: number;
  constructor(status: number, message: string) {
    super(message);
    this.name = "EconomyRequestError";
    this.status = status;
  }
}

async function req<T>(path: string, init?: RequestInit): Promise<T> {
  // authedRawFetch: device cookie + selector `X-Active-Account` (cookie-BFF
  // multi-cuenta) en web, Bearer inyectado por Rust en native. Un fetch pelado
  // aquí respondía 401 (sin selector no hay rama de cuenta autenticada).
  const res = await authedRawFetch(economyUrl(path), {
    headers: { "content-type": "application/json" },
    ...init,
  });
  if (!res.ok) {
    // El cuerpo de error del servidor es texto plano (AppError). Se propaga para
    // que la UI muestre "saldo insuficiente" / "tope alcanzado" / "ya reclamada".
    const body = await res.text().catch(() => "");
    throw new EconomyRequestError(res.status, body || `HTTP ${res.status}`);
  }
  // Frontera JSON: la forma la garantiza el contrato REST (utoipa camelCase).
  // oxlint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
  return res.json() as Promise<T>;
}

/** Compra un pack de bellotas (IAP; el servidor acredita las bellotas del pack). */
export async function purchasePack(productId: string): Promise<WalletMutationResultDTO> {
  return req<WalletMutationResultDTO>("/purchase", {
    method: "POST",
    body: JSON.stringify({ productId }),
  });
}

/** Acredita la recompensa por vídeo (+10). El servidor aplica el tope diario. */
export async function watchRewardedVideo(): Promise<WalletMutationResultDTO> {
  return req<WalletMutationResultDTO>("/reward-video", { method: "POST" });
}

/** Reclama la recompensa diaria de fidelidad (+10). 409 si ya se reclamó hoy. */
export async function claimDailyReward(): Promise<DailyRewardResultDTO> {
  return req<DailyRewardResultDTO>("/daily-reward", { method: "POST" });
}

/** Canjea un marco/sticker por bellotas. 400 si saldo insuficiente. */
export async function redeemReward(productId: string): Promise<RedeemResultDTO> {
  return req<RedeemResultDTO>("/redeem", {
    method: "POST",
    body: JSON.stringify({ productId }),
  });
}
